<template>
  <div class="empty-dashboard-container">
    <div class="empty-state-content">
      <div class="illustration">
        <svg width="300" height="200" viewBox="0 0 300 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <!-- Cloud illustration -->
          <circle cx="150" cy="100" r="60" fill="#F0F7FF" opacity="0.5"/>
          <circle cx="120" cy="100" r="50" fill="#E3F2FF" opacity="0.7"/>
          <circle cx="180" cy="100" r="50" fill="#E3F2FF" opacity="0.7"/>

          <!-- Amazon logo placeholder -->
          <path d="M150 80 L160 95 L150 110 L140 95 Z" fill="#FF9900" opacity="0.3"/>

          <!-- Disconnected icon -->
          <line x1="130" y1="120" x2="170" y2="140" stroke="#CCC" stroke-width="3" stroke-dasharray="5,5"/>
        </svg>
      </div>

      <h2 class="empty-title">Connect Amazon to See Your Insights</h2>

      <p class="empty-description">
        Your dashboard is empty because we haven't connected to your Amazon account yet.
      </p>

      <div class="features-grid">
        <div class="feature-card">
          <q-icon name="bar_chart" size="32px" color="primary" />
          <h3>Sales Analytics</h3>
          <p>Track your sales performance in real-time</p>
        </div>

        <div class="feature-card">
          <q-icon name="attach_money" size="32px" color="primary" />
          <h3>Pricing Insights</h3>
          <p>Optimize prices automatically based on market data</p>
        </div>

        <div class="feature-card">
          <q-icon name="campaign" size="32px" color="primary" />
          <h3>Campaign Reports</h3>
          <p>Get detailed advertising performance reports</p>
        </div>

        <div class="feature-card">
          <q-icon name="notifications_active" size="32px" color="primary" />
          <h3>Performance Alerts</h3>
          <p>Receive notifications for important changes</p>
        </div>
      </div>

      <div class="action-buttons">
        <q-btn
          class="connect-btn"
          label="Connect Amazon Now"
          color="primary"
          size="lg"
          unelevated
          icon="link"
          @click="connectAmazon"
          :loading="connecting"
        />

        <q-btn
          class="demo-btn"
          label="See a Demo First"
          color="grey-7"
          size="lg"
          flat
          @click="showDemo"
        />
      </div>

      <div class="help-section">
        <p class="help-text">Already connected? <a @click="refreshConnection">Refresh</a></p>
      </div>
    </div>

    <!-- Demo Dialog -->
    <q-dialog v-model="showDemoDialog" :maximized="$q.screen.lt.md">
      <q-card class="demo-card">
        <q-card-section class="demo-header">
          <div class="demo-title">
            <h3>UniQin.ai Dashboard Demo</h3>
            <q-btn icon="close" flat round dense @click="showDemoDialog = false" />
          </div>
        </q-card-section>

        <q-card-section class="demo-content">
          <div class="demo-video">
            <!-- Placeholder for demo video or images -->
            <img src="@/assets/dashboard-demo.png" alt="Dashboard Demo" v-if="false" />
            <div class="video-placeholder">
              <q-icon name="play_circle_outline" size="80px" color="primary" />
              <p>Interactive demo coming soon!</p>
            </div>
          </div>

          <div class="demo-features">
            <h4>What You'll Get:</h4>
            <ul>
              <li>Real-time sales tracking across all your products</li>
              <li>Automated pricing optimization using AI</li>
              <li>Comprehensive advertising campaign analytics</li>
              <li>Inventory management and forecasting</li>
              <li>Competitor price monitoring</li>
              <li>Custom alerts and notifications</li>
            </ul>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            label="Connect My Account"
            color="primary"
            unelevated
            @click="connectAmazonFromDemo"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
export default {
  name: 'EmptyDashboardState',
  data() {
    return {
      connecting: false,
      showDemoDialog: false
    };
  },
  methods: {
    async connectAmazon() {
      this.connecting = true;

      try {
        // Track connection attempt
        if (this.$mixpanel) {
          this.$mixpanel.track('Amazon Connection Initiated', {
            UserEmail: localStorage.getItem('email'),
            Source: 'Empty Dashboard State'
          });
        }

        // Save context
        localStorage.setItem('connection_source', 'empty_dashboard');

        // Get Amazon OAuth configuration
        const amz_app_id = process.env.VUE_APP_AMZ_APP_ID || 'amzn1.sp.solution.220bca05-f442-4f7b-b14f-888f252964f3';
        const redirect_uri = encodeURIComponent(window.location.origin + '/amazonconnection');

        // Build OAuth URL
        const oauthUrl = `https://sellercentral.amazon.in/apps/authorize/consent?` +
          `application_id=${amz_app_id}&` +
          `version=beta&` +
          `redirect_uri=${redirect_uri}`;

        // Redirect to Amazon OAuth
        window.location.href = oauthUrl;

      } catch (error) {
        console.error('Error initiating Amazon connection:', error);
        this.connecting = false;

        this.$q.notify({
          type: 'negative',
          message: 'Failed to initiate connection. Please try again.'
        });
      }
    },

    showDemo() {
      this.showDemoDialog = true;

      if (this.$mixpanel) {
        this.$mixpanel.track('Demo Requested', {
          UserEmail: localStorage.getItem('email'),
          Source: 'Empty Dashboard State'
        });
      }
    },

    connectAmazonFromDemo() {
      this.showDemoDialog = false;
      this.connectAmazon();
    },

    async refreshConnection() {
      // Check if user has connected
      this.$q.loading.show({ message: 'Checking connection status...' });

      try {
        // Check backend for connection status
        const response = await this.$axios.get('/api/user/amazon-status');

        if (response.data.connected) {
          // Refresh the page to show data
          this.$q.notify({
            type: 'positive',
            message: 'Connection found! Refreshing dashboard...'
          });

          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          this.$q.notify({
            type: 'info',
            message: 'No connection found. Please connect your Amazon account.'
          });
        }
      } catch (error) {
        console.error('Error checking connection:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Could not check connection status.'
        });
      } finally {
        this.$q.loading.hide();
      }
    }
  }
};
</script>

<style scoped>
.empty-dashboard-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
  padding: 40px 20px;
  background: linear-gradient(135deg, #F0F7FF 0%, #FFFFFF 100%);
}

.empty-state-content {
  max-width: 900px;
  text-align: center;
}

.illustration {
  margin-bottom: 32px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.empty-title {
  font-size: 32px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
}

.empty-description {
  font-size: 16px;
  color: #666;
  margin-bottom: 40px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.feature-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.feature-card h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 16px 0 8px;
}

.feature-card p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
}

.connect-btn {
  min-width: 280px;
  height: 52px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  text-transform: none;
}

.demo-btn {
  min-width: 280px;
  height: 48px;
  font-size: 15px;
  text-transform: none;
}

.help-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #E0E0E0;
}

.help-text {
  font-size: 14px;
  color: #666;
}

.help-text a {
  color: #378CEF;
  cursor: pointer;
  text-decoration: underline;
}

.help-text a:hover {
  color: #2166B1;
}

/* Demo Dialog Styles */
.demo-card {
  max-width: 800px;
}

.demo-header {
  background: linear-gradient(135deg, #378CEF 0%, #2166B1 100%);
  color: white;
  padding: 24px;
}

.demo-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.demo-title h3 {
  margin: 0;
  font-size: 24px;
}

.demo-content {
  padding: 32px;
}

.video-placeholder {
  background: #F5F5F5;
  border-radius: 12px;
  padding: 80px 40px;
  text-align: center;
  margin-bottom: 32px;
}

.video-placeholder p {
  font-size: 16px;
  color: #666;
  margin-top: 16px;
}

.demo-features h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.demo-features ul {
  text-align: left;
  max-width: 600px;
  margin: 0 auto;
}

.demo-features li {
  font-size: 15px;
  color: #555;
  margin-bottom: 12px;
  line-height: 1.6;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .empty-dashboard-container {
    padding: 24px 16px;
  }

  .empty-title {
    font-size: 24px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .action-buttons {
    width: 100%;
  }

  .connect-btn,
  .demo-btn {
    min-width: 100%;
  }
}
</style>
